import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  getFirestore, 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs,
  serverTimestamp,
  query, 
  orderBy // Used for sorting
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

// Define a type for a single supplier object
interface Supplier {
  id: string;
  name: string;
  contactPerson: string;
  email: string;
  phone: string;
  address: string;
  products: string[];
  createdAt?: any;
  updatedAt?: any;
}

// Define a type for a single post object (from adminPosts)
interface Post {
  id: string;
  title: string;
  content: string;
  author: string;
  createdAt: any;
}

// UPDATED: Define a type for a single reply object (from replies)
interface Reply {
  id: string;
  postId: string;
  postTitle: string;
  userName: string;
  userEmail: string;
  userPhone: string;
  message: string;
  replyMethod: 'whatsapp' | 'db_only'; 
  repliedAt: any;
  // Status will be used for sorting: 'new' comes before 'read'
  status: 'new' | 'read' | 'archived'; 
}

// Styled components (omitted for brevity, assume they are the same as provided)
const Container = styled.div`
  padding: 2rem;
  background-color: #f0f2f5;
  min-height: 100vh;
`;

const Header = styled.h1`
  color: #333;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #ddd;
  padding-bottom: 0.5rem;
`;

const SectionHeader = styled.h2`
  color: #555;
  margin-top: 2rem;
  margin-bottom: 1rem;
`;

const FormContainer = styled.div`
  background-color: #fff;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const Form = styled.form`
  display: grid;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-weight: bold;
  margin-bottom: 0.5rem;
  color: #555;
`;

const Input = styled.input`
  padding: 0.8rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
`;

const Textarea = styled.textarea`
  padding: 0.8rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
  min-height: 100px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const SubmitButton = styled.button`
  padding: 0.8rem 1.5rem;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color 0.3s;

  &:hover {
    background-color: #0056b3;
  }

  &:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(SubmitButton)`
  background-color: #6c757d;

  &:hover {
    background-color: #5a6268;
  }
`;

const AddButton = styled(SubmitButton)`
  background-color: #28a745;
  margin-bottom: 1rem;

  &:hover {
    background-color: #218838;
  }
`;

const TableContainer = styled.div`
  background-color: #fff;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow-x: auto;
  margin-bottom: 2rem; 
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1.5rem;
`;

const TableHead = styled.thead`
  background-color: #f8f9fa;
  color: #495057;
`;

const TableRow = styled.tr<{ isNew?: boolean }>`
  &:nth-child(even) {
    background-color: #f2f2f2;
  }
  /* Highlight rows with 'new' status */
  ${props => props.isNew && `
    background-color: #fff3cd; /* Light yellow background */
    border-left: 5px solid #ffc107; /* Yellow highlight bar */
    font-weight: bold;
    &:nth-child(even) {
      background-color: #fff3cd; 
    }
  `}
`;

const TableHeader = styled.th`
  padding: 1rem;
  text-align: left;
  border-bottom: 2px solid #dee2e6;
`;

const TableCell = styled.td`
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
  max-width: 250px; 
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: #007bff;
  cursor: pointer;
  margin-right: 0.5rem;
  font-size: 1rem;

  &:hover {
    text-decoration: underline;
  }

  &:disabled {
    color: #6c757d;
    cursor: not-allowed;
  }
`;

const ToggleStatusButton = styled(ActionButton)<{ isRead: boolean }>`
  color: ${props => props.isRead ? '#17a2b8' : '#28a745'};
  font-weight: bold;
  /* Use an emoji for better visual distinction */
  &::before {
    content: '${props => props.isRead ? '✅ ' : '📧 '} ';
  }
`;

const DeleteButton = styled(ActionButton)`
  color: #dc3545;

  &:hover {
    color: #c82333;
  }

  &:disabled {
    color: #6c757d;
  }
`;

const Message = styled.p<{ type: 'success' | 'error' | 'info' }>`
  text-align: center;
  margin-top: 1rem;
  padding: 0.8rem;
  border-radius: 4px;
  color: white;
  background-color: ${props => (props.type === 'success' ? '#28a745' : props.type === 'error' ? '#dc3545' : '#17a2b8')};
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #6c757d;
  font-style: italic;
`;


const SuppliersManagement: React.FC = () => {
  // Supplier management state
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [currentSupplier, setCurrentSupplier] = useState<Supplier | null>(null);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);

  // Post management state
  const [postTitle, setPostTitle] = useState('');
  const [postContent, setPostContent] = useState('');
  const [postMessage, setPostMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [loadingPost, setLoadingPost] = useState(false);

  // Reply/Feedback management state
  const [replies, setReplies] = useState<Reply[]>([]);
  const [loadingReplies, setLoadingReplies] = useState(false);

  // Get Firebase services
  const db = getFirestore();
  const auth = getAuth();

  // Helper function to format timestamps
  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    try {
      if (timestamp.toDate) {
        return timestamp.toDate().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      return new Date(timestamp).toLocaleDateString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Load suppliers from Firebase (omitted, no change)
  const loadSuppliers = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'suppliers'));
      const suppliersData: Supplier[] = [];
      querySnapshot.forEach((doc) => {
        suppliersData.push({ id: doc.id, ...doc.data() } as Supplier);
      });
      setSuppliers(suppliersData);
    } catch (error) {
      console.error('Error loading suppliers: ', error);
      setMessage({ text: 'Failed to load suppliers', type: 'error' });
    } finally {
      setLoading(false);
    }
  };
  
  // MODIFIED: Load replies (feedback) from Firebase with better error handling
  const loadReplies = async () => {
    setLoadingReplies(true);
    try {
      // Query: Order by status ('new' asc) then by repliedAt (desc).
      const q = query(
        collection(db, 'replies'), 
        orderBy('status', 'asc'), 
        orderBy('repliedAt', 'desc') 
      );
      
      const querySnapshot = await getDocs(q);
      const repliesData: Reply[] = [];

      // Log the number of documents found for debugging
      console.log(`[Firestore Debug] Found ${querySnapshot.size} replies.`);
      if (querySnapshot.empty) {
        console.log("[Firestore Debug] Query returned empty. Check for missing data or a missing composite index.");
      }
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        repliesData.push({ 
          id: doc.id, 
          // Ensure 'status' defaults to 'new' if not present for older entries
          status: data.status || 'new', 
          ...data 
        } as Reply);
      });
      setReplies(repliesData);
      setMessage(null); // Clear error message on success

    } catch (error) {
      console.error('CRITICAL Error loading replies. Check Firebase console for Index requirement:', error);
      setMessage({ 
        text: "Failed to load feedback! Check your browser console for an index link. You must create a composite index for 'status' (asc) and 'repliedAt' (desc).", 
        type: 'error' 
      });
    } finally {
      setLoadingReplies(false);
    }
  };

  // Function to toggle the read status
  const handleToggleStatus = async (replyId: string, currentStatus: 'new' | 'read' | 'archived') => {
    const newStatus = currentStatus === 'new' ? 'read' : 'new';
    setLoadingReplies(true); 

    try {
      const replyRef = doc(db, 'replies', replyId);
      await updateDoc(replyRef, {
        status: newStatus,
        updatedAt: serverTimestamp(), // Optional: track when status was changed
      });
      
      // We rely on loadReplies() to update the state correctly with sorting
      await loadReplies(); 

    } catch (error) {
      console.error('Error updating reply status: ', error);
      setMessage({ text: 'Failed to update reply status.', type: 'error' });
    } finally {
      // setLoadingReplies(false) is handled by loadReplies()
    }
  };


  useEffect(() => {
    loadSuppliers();
    loadReplies(); 
  }, []);

  // Handle CRUD operations for suppliers and posts (omitted for brevity, no change)
  const handleSaveSupplier = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!currentSupplier) return;

    setLoading(true);
    try {
      if (currentSupplier.id) {
        // Update existing supplier in Firebase
        const supplierRef = doc(db, 'suppliers', currentSupplier.id);
        await updateDoc(supplierRef, {
          ...currentSupplier,
          updatedAt: serverTimestamp(),
        });
        setMessage({ text: 'Supplier updated successfully!', type: 'success' });
      } else {
        // Create new supplier in Firebase
        const docRef = await addDoc(collection(db, 'suppliers'), {
          ...currentSupplier,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        setMessage({ text: 'Supplier added successfully!', type: 'success' });
      }
      
      // Reload suppliers to get the latest data
      await loadSuppliers();
      
      // Reset form and hide it
      setCurrentSupplier(null);
      setIsFormVisible(false);
    } catch (error) {
      console.error('Error saving supplier: ', error);
      setMessage({ text: 'Failed to save supplier', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleEditSupplier = (supplier: Supplier) => {
    setCurrentSupplier(supplier);
    setIsFormVisible(true);
  };

  const handleDeleteSupplier = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this supplier?')) {
      setLoading(true);
      try {
        await deleteDoc(doc(db, 'suppliers', id));
        setSuppliers(suppliers.filter(supplier => supplier.id !== id));
        setMessage({ text: 'Supplier deleted successfully!', type: 'success' });
      } catch (error) {
        console.error('Error deleting supplier: ', error);
        setMessage({ text: 'Failed to delete supplier', type: 'error' });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleAddSupplier = () => {
    setCurrentSupplier({
      id: '',
      name: '',
      contactPerson: '',
      email: '',
      phone: '',
      address: '',
      products: [],
    });
    setIsFormVisible(true);
  };

  const handlePostSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoadingPost(true);
    setPostMessage(null);

    const user = auth.currentUser;
    if (!user) {
      setPostMessage({ text: 'You must be logged in to post.', type: 'error' });
      setLoadingPost(false);
      return;
    }

    try {
      await addDoc(collection(db, 'adminPosts'), {
        title: postTitle,
        content: postContent,
        author: user.email,
        createdAt: serverTimestamp(),
        category: 'supplier',
      });

      setPostMessage({ text: 'Post successfully submitted!', type: 'success' });
      setPostTitle('');
      setPostContent('');

    } catch (error) {
      console.error('Error adding document: ', error);
      setPostMessage({ text: 'Failed to submit post. Please try again.', type: 'error' });
    } finally {
      setLoadingPost(false);
    }
  };


  return (
    <Container>
      <Header>Supplier Management</Header>

      {/* Display messages */}
      {message && (
        <Message type={message.type} onClick={() => setMessage(null)} style={{ cursor: 'pointer' }}>
          {message.text}
        </Message>
      )}

      {/* Admin Posting Section */}
      <SectionHeader>Post an Announcement</SectionHeader>
      <FormContainer>
        <form onSubmit={handlePostSubmit}>
          <FormGroup>
            <Label htmlFor="post-title">Title</Label>
            <Input
              id="post-title"
              type="text"
              value={postTitle}
              onChange={(e) => setPostTitle(e.target.value)}
              placeholder="Enter the post title"
              required
            />
          </FormGroup>
          <FormGroup>
            <Label htmlFor="post-content">Content</Label>
            <Textarea
              id="post-content"
              value={postContent}
              onChange={(e) => setPostContent(e.target.value)}
              placeholder="Write your content here..."
              required
            />
          </FormGroup>
          <SubmitButton type="submit" disabled={loadingPost}>
            {loadingPost ? 'Submitting...' : 'Publish Post'}
          </SubmitButton>
        </form>
        {postMessage && <Message type={postMessage.type}>{postMessage.text}</Message>}
      </FormContainer>
      
      {/* Horizontal line to separate sections */}
      <hr style={{ margin: '3rem 0', borderColor: '#ddd' }} />

      {/* Submitted Feedback/Replies Section */}
      <SectionHeader>Submitted Feedback & Replies ({replies.length} total)</SectionHeader>

      <TableContainer>
        {loadingReplies ? (
          <LoadingMessage>Loading submitted feedback...</LoadingMessage>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>Status</TableHeader>
                <TableHeader>Date</TableHeader>
                <TableHeader>Post Title</TableHeader>
                <TableHeader>User</TableHeader>
                <TableHeader>Method</TableHeader>
                <TableHeader>Message Snippet</TableHeader>
                <TableHeader>Action</TableHeader>
              </TableRow>
            </TableHead>
            <tbody>
              {replies.length > 0 ? (
                replies.map(reply => (
                  // Use isNew prop to apply styling
                  <TableRow key={reply.id} isNew={reply.status === 'new'}> 
                    <TableCell>
                      {/* Display status with a badge/color */}
                      <span style={{
                        color: reply.status === 'new' ? '#dc3545' : '#28a745',
                        fontWeight: 'bold'
                      }}>
                        {reply.status.toUpperCase()}
                      </span>
                    </TableCell>
                    <TableCell>{formatDate(reply.repliedAt)}</TableCell>
                    <TableCell>{reply.postTitle}</TableCell>
                    <TableCell>
                      {reply.userName}<br/>
                      <small>{reply.userEmail}</small>
                    </TableCell>
                    <TableCell>
                      {reply.replyMethod === 'db_only' ? 'Admin DB' : 'WhatsApp'}
                    </TableCell>
                    {/* Use title attribute to show full message on hover */}
                    <TableCell title={reply.message}>
                      {reply.message.substring(0, 50)}{reply.message.length > 50 ? '...' : ''}
                    </TableCell>
                    {/* Toggle Status Button */}
                    <TableCell>
                      <ToggleStatusButton 
                        isRead={reply.status === 'read'}
                        onClick={() => handleToggleStatus(reply.id, reply.status)}
                        disabled={loadingReplies}
                      >
                        {reply.status === 'new' ? 'Mark Read' : 'Mark Unread'}
                      </ToggleStatusButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} style={{ textAlign: 'center' }}>No feedback has been submitted yet.</TableCell>
                </TableRow>
              )}
            </tbody>
          </Table>
        )}
      </TableContainer>

      {/* Horizontal line to separate sections */}
      <hr style={{ margin: '3rem 0', borderColor: '#ddd' }} />

      {/* Supplier Management CRUD Section */}
      <SectionHeader>Manage Suppliers</SectionHeader>
      
      {isFormVisible ? (
        <FormContainer>
          <h3>{currentSupplier?.id ? 'Edit Supplier' : 'Add New Supplier'}</h3>
          <Form onSubmit={handleSaveSupplier}>
            <FormGroup>
              <Label htmlFor="name">Supplier Name</Label>
              <Input
                id="name"
                type="text"
                value={currentSupplier?.name || ''}
                onChange={(e) => setCurrentSupplier({ ...currentSupplier!, name: e.target.value })}
                required
                disabled={loading}
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="contactPerson">Contact Person</Label>
              <Input
                id="contactPerson"
                type="text"
                value={currentSupplier?.contactPerson || ''}
                onChange={(e) => setCurrentSupplier({ ...currentSupplier!, contactPerson: e.target.value })}
                required
                disabled={loading}
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={currentSupplier?.email || ''}
                onChange={(e) => setCurrentSupplier({ ...currentSupplier!, email: e.target.value })}
                required
                disabled={loading}
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                type="tel"
                value={currentSupplier?.phone || ''}
                onChange={(e) => setCurrentSupplier({ ...currentSupplier!, phone: e.target.value })}
                disabled={loading}
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="address">Address</Label>
              <Textarea
                id="address"
                value={currentSupplier?.address || ''}
                onChange={(e) => setCurrentSupplier({ ...currentSupplier!, address: e.target.value })}
                disabled={loading}
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="products">Products (comma separated)</Label>
              <Input
                id="products"
                type="text"
                value={currentSupplier?.products.join(', ') || ''}
                onChange={(e) => setCurrentSupplier({ ...currentSupplier!, products: e.target.value.split(',').map(p => p.trim()) })}
                disabled={loading}
              />
            </FormGroup>
            <ButtonGroup>
              <CancelButton 
                type="button" 
                onClick={() => setIsFormVisible(false)}
                disabled={loading}
              >
                Cancel
              </CancelButton>
              <SubmitButton type="submit" disabled={loading}>
                {loading ? 'Saving...' : currentSupplier?.id ? 'Update Supplier' : 'Add Supplier'}
              </SubmitButton>
            </ButtonGroup>
          </Form>
        </FormContainer>
      ) : (
        <>
          <AddButton onClick={handleAddSupplier} disabled={loading}>
            + Add New Supplier
          </AddButton>
          
          <TableContainer>
            {loading ? (
              <LoadingMessage>Loading suppliers...</LoadingMessage>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>Name</TableHeader>
                    <TableHeader>Contact Person</TableHeader>
                    <TableHeader>Email</TableHeader>
                    <TableHeader>Phone</TableHeader>
                    <TableHeader>Products</TableHeader>
                    <TableHeader>Actions</TableHeader>
                  </TableRow>
                </TableHead>
                <tbody>
                  {suppliers.map(supplier => (
                    <TableRow key={supplier.id}>
                      <TableCell>{supplier.name}</TableCell>
                      <TableCell>{supplier.contactPerson}</TableCell>
                      <TableCell>{supplier.email}</TableCell>
                      <TableCell>{supplier.phone}</TableCell>
                      <TableCell>{supplier.products.join(', ')}</TableCell>
                      <TableCell>
                        <ActionButton 
                          onClick={() => handleEditSupplier(supplier)}
                          disabled={loading}
                        >
                          Edit
                        </ActionButton>
                        <DeleteButton 
                          onClick={() => handleDeleteSupplier(supplier.id)}
                          disabled={loading}
                        >
                          Delete
                        </DeleteButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            )}
            {suppliers.length === 0 && !loading && (
              <LoadingMessage>No suppliers found. Add your first supplier!</LoadingMessage>
            )}
          </TableContainer>
        </>
      )}
    </Container>
  );
};

export default SuppliersManagement;